import classcat from "classcat";
import React from "react";

import { Button, IconArchive, IconCart } from "@touchtech/web-baselayer-ui";

import BasketItems from "@is/components/BasketItems";
import DrawerActionButton from "@is/components/DrawerActionButton";
import {
  CHECKOUT_CONFIGURATIONS_TYPE_ICONS,
  CheckoutConfigurationType,
} from "@is/constants";
import FailedConfirmationModal from "@is/features/CheckoutDrawer/FailedConfirmationModal";
import WidgetItem from "@is/features/ContentView/WidgetItem";

import css from "./BasketDrawerContent.module.css";
import ParkBasketModal from "./ParkBasketModal";
import useManageBasketDrawerContent from "./hooks/useManageBasketDrawerContent";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onGoToCheckout: (
    checkoutConfigurationsType: CheckoutConfigurationType
  ) => void;
};

const BasketDrawerContent = ({ isOpen, onClose, onGoToCheckout }: Props) => {
  const {
    basketItems,
    isScrolled,
    isCheckoutDisabled,
    basketWidget,
    handleShowMoreWidgetItems,
    handleShowAllWidgetItems,
    handleScroll,
    setIsCheckoutDisabled,
    checkoutConfigurations,
    handleGoToCheckout,
    t,
    enabledQRCodeCheckoutConfiguration,
    isParkBasketModalOpen,
    setIsParkBasketModalOpen,
    handleOpenParkBasketModal,
    isFailedModalOpen,
    setIsFailedModalOpen,
    handleOpenFailedModal,
    restoredParkedBasketName,
  } = useManageBasketDrawerContent({ isOpen, onGoToCheckout });

  return (
    <>
      <DrawerActionButton onClick={onClose} isScrolled={isScrolled} />
      <div className={css.container} onScroll={handleScroll}>
        <div className={css.spaceBetween}>
          <div className={css.basketContainer}>
            <div>
              <div className={classcat(["text-headline-5xl", css.title])}>
                <strong>{t("Basket")}</strong>
              </div>
              <BasketItems
                setItemPending={setIsCheckoutDisabled}
                enabledQRCodeCheckoutConfiguration={
                  enabledQRCodeCheckoutConfiguration
                }
              />
              <div className={css.checkoutButtonsContainer}>
                {basketItems?.length ? (
                  <>
                    {checkoutConfigurations?.map((configuration) => (
                      <Button
                        key={configuration.type}
                        onClick={handleGoToCheckout}
                        sizing="lg"
                        variant="primary"
                        state={isCheckoutDisabled ? "disabled" : undefined}
                        width="full"
                        name={configuration.type}
                      >
                        {CHECKOUT_CONFIGURATIONS_TYPE_ICONS[configuration.type]}
                        {t(configuration.label)}
                      </Button>
                    ))}
                  </>
                ) : (
                  <Button
                    onClick={onClose}
                    sizing="lg"
                    variant="primary"
                    width="full"
                  >
                    <IconCart />
                    {t("Continue shopping")}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className={css.widgetContainer}>
            {!!basketWidget && (
              <WidgetItem
                className={css.widget}
                widget={basketWidget}
                onShowMore={handleShowMoreWidgetItems}
                onShowAll={handleShowAllWidgetItems}
                isBasketWidget
              />
            )}
          </div>
        </div>
        <div className={css.parkContainer}>
          <Button
            variant="tertiary"
            sizing="lg"
            width="full"
            className="text-button-lg"
            state={
              !basketItems?.length || isCheckoutDisabled
                ? "disabled"
                : undefined
            }
            onClick={handleOpenParkBasketModal}
          >
            <IconArchive
              className={classcat([
                {
                  [css.disabledIcon]:
                    !basketItems?.length || isCheckoutDisabled,
                },
                "text-label-2xl",
              ])}
            />
            {t("Park basket")}
          </Button>
        </div>

        <ParkBasketModal
          isOpen={isParkBasketModalOpen}
          setIsOpen={setIsParkBasketModalOpen}
          openFailedModal={handleOpenFailedModal}
          sessionName={restoredParkedBasketName}
        />

        <FailedConfirmationModal
          isOpen={isFailedModalOpen}
          setIsOpen={setIsFailedModalOpen}
          onClose={handleOpenParkBasketModal}
          errorMessage={t("Please try again.")}
        />
      </div>
    </>
  );
};

export default BasketDrawerContent;
